/**
 * dayjs タイムゾーン一元管理モジュール
 *
 * 海外端末（ハワイ等）からのアクセスでも常に Asia/Tokyo で
 * パース・フォーマットされるよう、timezone プラグインを設定済みの
 * ラッパー関数をエクスポートする。
 *
 * 使い方:
 *   import { dayjsTz as dayjs } from '~/utils/dayjs'
 *   dayjs('2026-04-03T14:00:00+09:00').format('HH:mm') // → "14:00"（どの端末 TZ でも）
 *   dayjs('2026-04-03 14:00').format()                  // → "2026-04-03T14:00:00+09:00"（JST として解釈）
 *
 * cmp 内で生の `import dayjs from 'dayjs'` は使わず、必ずこのモジュールを使う。
 * プラグインもここに集約する（各ファイルで dayjs.extend しない）。
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import minMax from 'dayjs/plugin/minMax'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isoWeek from 'dayjs/plugin/isoWeek'
import 'dayjs/locale/ja'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(minMax)
dayjs.extend(customParseFormat)
dayjs.extend(isoWeek)
dayjs.locale('ja')
dayjs.tz.setDefault('Asia/Tokyo')

const TZ = 'Asia/Tokyo'
// dayjs 本体（REGEX_PARSE）と同じ「ISO 風でオフセットなし」の判定。
// これに一致する文字列だけを JST の壁時計として読む（'2026-05-01', '2026-05-01 10:00', '2026/5/1 9:00', '2026-05-01T09:00:00' 等）。
// オフセット付き（+09:00 / Z）・RFC 2822（'Wed, 30 Sep 2026 01:00:00 GMT'）・'... UTC'・String(Date) はここに掛からず、
// 絶対時刻として dayjs(str) → JST 変換に回す（壁時計として読むと日本端末でも 9 時間ずれる）
const ISO_LIKE_NO_OFFSET = /^(\d{4})[-/]?(\d{1,2})?[-/]?(\d{0,2})[Tt\s]*(\d{1,2})?:?(\d{1,2})?:?(\d{1,2})?[.:]?(\d+)?$/

/**
 * Asia/Tokyo タイムゾーンでパース・フォーマットする dayjs ラッパー
 * - undefined: 現在時刻（JST）
 * - null: Invalid（素の dayjs(null) と同じ。「値なし」を現在時刻にしない）
 * - オフセットなしの ISO 風文字列(YYYY-MM-DD, YYYY-MM-DD HH:mm 等): dayjs.tz(time) で JST の壁時計として直接パース
 *   （invalid 文字列で throw するので dayjs(time).tz() にフォールバック）
 * - それ以外の文字列（+09:00 / Z 付き、RFC 2822、String(Date) 等）: dayjs(time).tz() で絶対時刻として JST に変換
 *   （dayjs.tz(str, tz) はオフセットを無視するので使わない）
 * - Date / number / dayjs: 絶対時刻として JST に変換
 */
export function dayjsTz (time) {
  if (time === undefined) { return dayjs().tz(TZ) }
  if (time === null) { return dayjs(null) }
  if (typeof time === 'string' && ISO_LIKE_NO_OFFSET.test(time)) {
    try {
      const result = dayjs.tz(time, TZ)
      if (result.isValid()) { return result }
    } catch (e) {
      // fall through
    }
  }
  return dayjs(time).tz(TZ)
}

/**
 * FullCalendar（timeZone: 'Asia/Tokyo'・timezone プラグインなし）が返す Date を JST の dayjs にする。
 * view.currentStart / currentEnd / activeStart / activeEnd、selectInfo.start などは
 * 「UTC フィールド＝カレンダー上の日時」の Date（JST 14:00 が 14:00Z）なので、そのまま dayjsTz(Date) に
 * 渡すと絶対時刻として +9 時間ずれる。UTC フィールドをオフセットなし文字列にしてから JST として読む。
 * （startStr / endStr がある場面ではそちらを dayjsTz に渡すほうがよい）
 */
export function fromCalendarDate (date) {
  if (date === undefined || date === null) { return dayjs(null) } // Invalid（値なしを現在時刻にしない）
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) { return dayjs(NaN) }
  return dayjsTz(d.toISOString().slice(0, 19))
}

// dayjsTz に dayjs の静的メソッドをコピー（isDayjs, tz, utc, min, max, extend, locale 等）
Object.keys(dayjs).forEach((key) => {
  dayjsTz[key] = dayjs[key]
})

export { dayjs as rawDayjs }
export default dayjsTz
